<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import messages from './locales/messages';
import { SALON_PHOTO_2026_CODE as CODE, useSalonPhoto2026Active } from './period';

// Remise Salon de la Photo 2026 (pas de stand cette année, uniquement le code promo)
const props = defineProps({
    variant: {
        type: String as () => 'slide' | 'bar' | 'cart',
        default: 'bar'
    }
});
const emits = defineEmits(['apply']);

const { t } = useI18n({
    useScope: 'global',
    messages
});
const toast = useToast();
const isActive = useSalonPhoto2026Active();

function copyCode() {
    navigator.clipboard?.writeText(CODE).then(() => {
        toast.success(t('salon2026.copied'));
    }).catch(() => {});
}
</script>

<template>
    <template v-if="isActive">
        <div v-if="variant === 'slide'" class="salon2026-slide">
            <p class="tagline tagline-side">
                {{ t('salon2026.no_booth') }}
                <span class="red">{{ t('salon2026.discounts_here') }}</span>
            </p>
            <div class="center">
                <p class="event">{{ t('salon2026.event') }}</p>
                <p class="discount">{{ t('salon2026.discount') }}</p>
                <button type="button" class="code-pill" @click="copyCode">
                    <span class="label">{{ t('salon2026.code') }}</span>
                    <strong>{{ CODE }}</strong>
                </button>
                <p class="tagline tagline-center">
                    {{ t('salon2026.no_booth') }}
                    <span class="red">{{ t('salon2026.discounts_here') }}</span>
                </p>
            </div>
            <img class="filter" src="@/assets/promo/salon2026-filter.webp" alt="" />
        </div>

        <div v-else-if="variant === 'bar'" class="salon2026-bar">
            <span class="event">{{ t('salon2026.event') }}</span>
            <span>
                <strong class="discount">{{ t('salon2026.discount') }}</strong>
                {{ t('salon2026.with_code') }}
                <button type="button" class="code" @click="copyCode">{{ CODE }}</button>
            </span>
        </div>

        <div v-else-if="variant === 'cart'" class="salon2026-cart">
            <div>
                <p class="event">{{ t('salon2026.event') }}</p>
                <p>
                    <strong class="discount">{{ t('salon2026.discount') }}</strong>
                    {{ t('salon2026.with_code') }} <strong>{{ CODE }}</strong>
                </p>
            </div>
            <button type="button" class="apply" @click="emits('apply', CODE)">{{ t('salon2026.apply') }}</button>
        </div>
    </template>
</template>

<style lang="scss" scoped>
$salon-bg: #131415;
$salon-red: #DC0303;

p{
    margin: 0;
}
.red{
    color: $salon-red;
}
.discount{
    white-space: nowrap;
}

.salon2026-slide{
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 50vh;
    min-height: 340px;
    background-color: $salon-bg;
    color: #FFF;
    font-family: Montserrat, sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    .center{
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
    }
    .event{
        font-size: .8rem;
        font-weight: 600;
        color: #DDD;
    }
    .discount{
        font-size: 3.5rem;
        font-weight: 700;
        line-height: 1;
        margin: .4rem 0 .8rem 0;
        letter-spacing: -2px;
    }
    .code-pill{
        display: inline-flex;
        align-items: baseline;
        gap: .6rem;
        background-color: $salon-red;
        color: #FFF;
        border-radius: 999px;
        padding: .3rem 1.1rem;
        cursor: pointer;
        .label{
            font-size: .75rem;
            font-weight: 400;
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        strong{
            font-size: 1.2rem;
            font-weight: 700;
            letter-spacing: 1px;
        }
    }
    .tagline{
        font-weight: 700;
        font-size: .95rem;
        line-height: 1.3;
        .red{
            display: block;
        }
    }
    .tagline-side{
        display: none;
    }
    .tagline-center{
        margin-top: 1rem;
        max-width: 18rem;
    }
    .filter{
        position: absolute;
        right: -20%;
        bottom: -25%;
        width: 70%;
        max-width: 320px;
        opacity: .5;
        pointer-events: none;
    }
}

.salon2026-bar{
    width: 100%;
    background-color: $salon-bg;
    color: #FFF;
    font-family: Montserrat, sans-serif;
    font-size: 13px;
    padding: 6px 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 12px;
    text-align: center;
    .event{
        display: none;
        color: #CCC;
    }
    .discount{
        font-size: 15px;
    }
    .code{
        background-color: $salon-red;
        color: #FFF;
        font-weight: 700;
        letter-spacing: 1px;
        border-radius: 999px;
        padding: 1px 10px;
        margin-left: 4px;
        cursor: pointer;
    }
}

.salon2026-cart{
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    padding: 10px 12px;
    border: 1px solid $salon-red;
    border-radius: 8px;
    background-color: $salon-bg;
    color: #FFF;
    font-family: Montserrat, sans-serif;
    font-size: 13px;
    text-align: left;
    .event{
        font-size: 11px;
        color: #CCC;
        margin-bottom: 2px;
    }
    .discount{
        color: $salon-red;
        font-size: 15px;
    }
    .apply{
        flex-shrink: 0;
        background-color: $salon-red;
        color: #FFF;
        border-radius: 17px;
        padding: 6px 16px;
        cursor: pointer;
    }
}

@media only screen and (min-width : $responsive-point) {
    .salon2026-slide{
        justify-content: space-between;
        // le bloc .products-families de l'accueil remonte de 100px sur le carrousel
        padding: 1.5rem 6% calc(100px + 1.5rem) 6%;
        .event{
            font-size: 1rem;
        }
        .discount{
            font-size: clamp(4rem, 12vh, 8rem);
            margin: .5rem 0 1rem 0;
        }
        .code-pill{
            padding: .4rem 1.4rem;
            .label{
                font-size: .85rem;
            }
            strong{
                font-size: 1.6rem;
            }
        }
        .tagline-side{
            display: block;
            flex: 1;
            font-size: 1.6rem;
            max-width: 26rem;
        }
        .tagline-center{
            display: none;
        }
        .center{
            flex: 1;
        }
        .filter{
            position: static;
            flex: 1;
            width: auto;
            min-width: 0;
            max-width: 22rem;
            max-height: 100%;
            object-fit: contain;
            opacity: 1;
        }
    }
    .salon2026-bar .event{
        display: inline;
    }
}
</style>
